import React, { createContext, useContext, useMemo } from 'react';
import { todayISO } from '../domain/clock';
import { serverTimestamp } from 'firebase/firestore';
import { writeBatch } from './writes';
import { docRef, COLLECTIONS } from './paths';
import { db } from '../lib/firebase';
import { useAuth } from '../context/AuthContext';
import { freeId } from '../domain/freeId';
import { useCollectionWindow } from './useCollectionWindow';
import {
  repertoirePieceDocument, repertoireStatusDocument, sortRepertoire,
} from '../domain/repertoire';

// Repertoire assigned to a student. One document per piece per student.
//
// Status is the useful field: a teacher wants to know at a glance what is being
// worked on versus what is finished, and the report is mostly a roll-up of it.
//
// The statuses, the documents and the order are src/domain/repertoire.js; this
// provider keeps the reads and the batches.

const RepertoireContext = createContext(null);
const TRACK_LIMIT = 5000;

export function RepertoireProvider({ children }) {
  const { user } = useAuth();

  // The repertoire window, from whichever read path serves this session (#207
  // U5). `withMetadata` keeps the snapshot path's `includeMetadataChanges` as
  // the rollback; `pendingWrite` is spread by the default row.
  const read = useCollectionWindow({
    uid: user?.uid,
    collection: COLLECTIONS.repertoire,
    limit: TRACK_LIMIT,
    withMetadata: true,
  });
  const items = read.docs;

  const byStudent = useMemo(() => {
    const m = new Map();
    for (const r of items) {
      if (!m.has(r.studentId)) m.set(r.studentId, []);
      m.get(r.studentId).push(r);
    }
    for (const [id, list] of m) m.set(id, sortRepertoire(list));
    return m;
  }, [items]);

  const value = useMemo(() => ({
    items,
    // Exposed because every window hook has it, not for any one caller;
    // nothing turns on this flag here (a delete asks the server, #318).
    loading: read.loading,
    // The read stopped before it answered (#647) — src/domain/reading.js.
    failed: read.failed,
    forStudent: (id) => byStudent.get(id) || [],

    addPiece: async ({ studentId, title, composer, status, note }) => {
      if (!user) return null;
      const id = freeId('rep');
      const batch = writeBatch(db);
      batch.set(
        docRef(user.uid, COLLECTIONS.repertoire, id),
        repertoirePieceDocument({ studentId, title, composer, status, note }, todayISO(), serverTimestamp()),
      );
      await batch.commit();
      return id;
    },

    setStatus: async (id, status) => {
      if (!user) return;
      const batch = writeBatch(db);
      batch.set(docRef(user.uid, COLLECTIONS.repertoire, id),
                repertoireStatusDocument(status, serverTimestamp()), { merge: true });
      await batch.commit();
    },

    removePiece: async (id) => {
      if (!user) return;
      const batch = writeBatch(db);
      batch.delete(docRef(user.uid, COLLECTIONS.repertoire, id));
      await batch.commit();
    },
  }), [items, read.loading, read.failed, byStudent, user]);

  return <RepertoireContext.Provider value={value}>{children}</RepertoireContext.Provider>;
}

export function useRepertoire() {
  const ctx = useContext(RepertoireContext);
  if (!ctx) throw new Error('useRepertoire must be used within a RepertoireProvider');
  return ctx;
}
